import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, errorText } from '@/lib/api'
import { useChannels, useTestChannel, type Channel } from '@/lib/alerts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'

function ChannelRow({ c }: { c: Channel }) {
  const test = useTestChannel()
  const qc = useQueryClient()
  const del = useMutation({
    mutationFn: (id: string) => api(`/api/channels/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  })
  async function onDelete() {
    // The channel's secret (Resend API key / Telegram bot token) is write-only:
    // the API never returns it, so a misclick here is unrecoverable.
    if (!confirm(`Delete notification channel "${c.name}"? Its API key cannot be recovered.`)) {
      return
    }
    try {
      await del.mutateAsync(c.id)
      toast.success(`Channel ${c.name} deleted`)
    } catch (e) {
      toast.error(errorText(e, 'Delete failed'))
    }
  }
  async function onTest() {
    try {
      await test.mutateAsync(c.name)
      toast.success(`Test notification sent via ${c.name}`)
    } catch (e) {
      toast.error(errorText(e, 'Test failed'))
    }
  }
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <Badge variant="outline">{c.kind}</Badge>
        <span className="font-medium">{c.name}</span>
        {!c.enabled && <Badge variant="secondary">disabled</Badge>}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => void onTest()} disabled={test.isPending}>
          Send test
        </Button>
        <Button size="sm" variant="ghost" onClick={() => void onDelete()} disabled={del.isPending}>
          Delete
        </Button>
      </div>
    </div>
  )
}

function AddResendForm() {
  const qc = useQueryClient()
  const [name, setName] = useState('resend-default')
  const [apiKey, setApiKey] = useState('')
  const [from, setFrom] = useState('zeus@zeus.sponge-theory.dev')
  const [to, setTo] = useState('')
  const create = useMutation({
    mutationFn: () =>
      api<Channel>('/api/channels', {
        method: 'POST',
        body: JSON.stringify({
          name,
          kind: 'resend',
          enabled: true,
          config: {
            api_key: apiKey,
            from,
            to: to.split(',').map((s) => s.trim()).filter(Boolean),
          },
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] })
      setApiKey('')
      setTo('')
      toast.success('Resend channel saved')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  })
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="r-name">Name</Label>
        <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-key">API key</Label>
        <Input id="r-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="re_..." />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-from">From address</Label>
        <Input id="r-from" value={from} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-to">To (comma-separated)</Label>
        <Input id="r-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ops@…, alerts@…" />
      </div>
      <Button onClick={() => void create.mutate()} disabled={create.isPending || !apiKey || !to}>
        Save Resend channel
      </Button>
    </div>
  )
}

function AddTelegramForm() {
  const qc = useQueryClient()
  const [name, setName] = useState('telegram-default')
  const [token, setToken] = useState('')
  const [chats, setChats] = useState('')
  const create = useMutation({
    mutationFn: () =>
      api<Channel>('/api/channels', {
        method: 'POST',
        body: JSON.stringify({
          name,
          kind: 'telegram',
          enabled: true,
          config: {
            bot_token: token,
            chat_ids: chats.split(',').map((s) => s.trim()).filter(Boolean),
          },
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] })
      setToken('')
      setChats('')
      toast.success('Telegram channel saved')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  })
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="t-name">Name</Label>
        <Input id="t-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="t-token">Bot token</Label>
        <Input id="t-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123:abc" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="t-chats">Chat IDs (comma-separated)</Label>
        <Input id="t-chats" value={chats} onChange={(e) => setChats(e.target.value)} placeholder="-1001234…, 987654…" />
      </div>
      <Button onClick={() => void create.mutate()} disabled={create.isPending || !token || !chats}>
        Save Telegram channel
      </Button>
    </div>
  )
}

export function SettingsNotificationsPage() {
  const { data, isLoading } = useChannels()
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Notification channels</h1>
        <p className="text-muted-foreground">
          Configure Resend (email) and Telegram destinations. Secrets are write-only — they are
          never returned by the API.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Existing channels</CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          {isLoading && <Skeleton className="h-20" />}
          {data?.length === 0 && (
            <p className="py-2 text-muted-foreground text-sm">No channels yet.</p>
          )}
          {data?.map((c) => <ChannelRow key={c.id} c={c} />)}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add Resend (email)</CardTitle>
          </CardHeader>
          <CardContent>
            <AddResendForm />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add Telegram</CardTitle>
          </CardHeader>
          <CardContent>
            <AddTelegramForm />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
